import { Skeleton } from "@/components/ui/skeleton";
import type { PlatformStats } from "@/lib/types/stats";
import type { PublicHomeCopy } from "./public-home-copy";
import { formatCount, formatHours, platformFigures, type PlatformFigure } from "./public-home-data";
import { ErrorLine, HomeSection, SectionHeading } from "./HomeParts";
import { useCountUp } from "./use-public-home";

interface PlatformFiguresProps {
  copy: PublicHomeCopy;
  lang: string;
  stats: PlatformStats | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

const FIGURE_COUNT = 5;

/**
 * The five live figures of GET /stats/platform. Skeletons while loading, an
 * error line with "Try again" when the call fails: never a misleading 0.
 */
export function PlatformFigures({ copy, lang, stats, isLoading, isError, onRetry }: PlatformFiguresProps) {
  const c = copy.figures;
  return (
    <HomeSection labelledBy="home-figures-title">
      <SectionHeading id="home-figures-title" eyebrow={c.eyebrow} title={c.title} />
      <div className="mt-10">
        {stats ? (
          <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-3 lg:grid-cols-5">
            {platformFigures(stats).map((figure) => (
              <Figure key={figure.key} figure={figure} label={c[figure.key]} unit={c.hoursUnit} lang={lang} />
            ))}
          </dl>
        ) : isError && !isLoading ? (
          <ErrorLine message={c.error} retryLabel={c.retry} onRetry={onRetry} />
        ) : (
          <div
            aria-busy="true"
            data-testid="figures-loading"
            className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-3 lg:grid-cols-5"
          >
            {Array.from({ length: FIGURE_COUNT }, (_, i) => (
              <div key={i} className="bg-background p-5 last:col-span-2 sm:p-6 lg:last:col-span-1">
                <Skeleton className="h-9 w-24" />
                <Skeleton className="mt-3 h-4 w-32 max-w-full" />
              </div>
            ))}
          </div>
        )}
      </div>
    </HomeSection>
  );
}

function Figure({ figure, label, unit, lang }: { figure: PlatformFigure; label: string; unit: string; lang: string }) {
  const shown = useCountUp(figure.value);
  const format = (value: number) => (figure.kind === "hours" ? `${formatHours(value, lang)} ${unit}` : formatCount(value, lang));
  return (
    <div className="flex min-w-0 flex-col-reverse justify-end bg-background p-5 last:col-span-2 sm:p-6 lg:last:col-span-1">
      <dt className="mt-2 text-sm leading-5 text-muted-foreground">{label}</dt>
      <dd className="text-3xl font-bold tabular-nums tracking-[-0.03em] text-foreground sm:text-4xl">
        <span className="sr-only">{format(figure.value)}</span>
        <span aria-hidden="true">{format(shown)}</span>
      </dd>
    </div>
  );
}
